import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError, fetchJSON } from "../api/client";
import { formatDuration, formatTime, trainLabel } from "../api/format";
import type { RouteResult } from "../api/types";
import { DirectionBadge } from "../components/Badges";
import { ErrorBanner } from "../components/ErrorBanner";
import { StationPicker } from "../components/StationPicker";
import { useCatalog } from "../context/CatalogContext";

export function FindTrains() {
  const { stations, error: catalogError, reload } = useCatalog();
  const [fromId, setFromId] = useState<number | null>(null);
  const [toId, setToId] = useState<number | null>(null);
  const [result, setResult] = useState<RouteResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(false);

  async function search(e: FormEvent) {
    e.preventDefault();
    if (fromId == null || toId == null) {
      setError(new ApiError("Pick both stations.", "invalid_id", 400));
      return;
    }
    if (fromId === toId) {
      setError(new ApiError("From and to cannot be the same station.", "same_station", 400));
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJSON<RouteResult>(`/v1/routes?from=${fromId}&to=${toId}`);
      setResult(data);
    } catch (err) {
      setResult(null);
      setError(err instanceof ApiError ? err : new ApiError(String(err), "internal", 0));
    } finally {
      setLoading(false);
    }
  }

  const trains = result?.trains ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Find trains</h1>
          <p className="muted">Directional: Multan → Karachi is not Karachi → Multan.</p>
        </div>
      </div>
      <ErrorBanner error={catalogError} onRetry={reload} />
      <form className="route-form" onSubmit={search}>
        <StationPicker stations={stations} value={fromId} onChange={setFromId} label="From" />
        <StationPicker stations={stations} value={toId} onChange={setToId} label="To" />
        <button type="submit" className="btn" disabled={loading}>
          {loading ? "Searching…" : "Search"}
        </button>
      </form>
      <ErrorBanner error={error} />
      {result && trains.length === 0 ? <p className="muted">No service between these stations.</p> : null}
      {trains.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Train</th>
                <th>Dir</th>
                <th>Depart</th>
                <th>Arrive</th>
                <th>Duration</th>
                <th>Stops between</th>
              </tr>
            </thead>
            <tbody>
              {trains.map((t) => (
                <tr key={t.train_id}>
                  <td>
                    <Link to={`/trains/${t.train_id}`}>{trainLabel(t)}</Link>
                  </td>
                  <td>
                    <DirectionBadge direction={t.direction} />
                  </td>
                  <td>
                    {t.from?.station_name}
                    <div className="muted">{formatTime(t.from?.departure_time || t.from?.arrival_time)}</div>
                  </td>
                  <td>
                    {t.to?.station_name}
                    <div className="muted">{formatTime(t.to?.arrival_time || t.to?.departure_time)}</div>
                  </td>
                  <td>{formatDuration(t.duration_minutes)}</td>
                  <td>{t.stops_between}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
